import Image from 'next/image';
import type { ReactNode } from 'react';

import type { PaymentMethodName } from '@/lib/order';
import { checkIsOneyPaymentByType } from '@/lib/payment/paymentTypeChecks';
import { PAYMENT_TYPES, type PaymentType } from '@/lib/payment/types';
import type { PaymentMethodConfiguration } from '@/states/paymentGatewaysState';

import { NextImage } from '../NextImage';

import { ReactComponent as Affirm } from '@/assets/icons/affirm.svg';
import { ReactComponent as Agos } from '@/assets/icons/agos.svg';
import { ReactComponent as Alipay } from '@/assets/icons/alipay.svg';
import { ReactComponent as AmericanExpress } from '@/assets/icons/americanexpress.svg';
import { ReactComponent as ApplePay } from '@/assets/icons/applepay.svg';
import { ReactComponent as Cembra } from '@/assets/icons/cembra.svg';
import { ReactComponent as CreditCard } from '@/assets/icons/credit-card.svg';
import { ReactComponent as CreditPlus } from '@/assets/icons/credit-plus.svg';
import { ReactComponent as Dinersclub } from '@/assets/icons/dinersclub.svg';
import DiscoverLogo from '@/assets/icons/discover.svg';
import { ReactComponent as GooglePay } from '@/assets/icons/google-pay.svg';
import { ReactComponent as Ideal } from '@/assets/icons/ideal.svg';
import { ReactComponent as JCB } from '@/assets/icons/jcb.svg';
import { ReactComponent as Klarna } from '@/assets/icons/klarna.svg';
import { ReactComponent as Mastercard } from '@/assets/icons/mastercard.svg';
import { ReactComponent as Oney } from '@/assets/icons/oney.svg';
import { ReactComponent as Paypal } from '@/assets/icons/paypal.svg';
import { ReactComponent as Smbc } from '@/assets/icons/smbc.svg';
import TwintLogo from '@/assets/icons/twint.svg';
import { ReactComponent as Unionpay } from '@/assets/icons/unionpay.svg';
import { ReactComponent as Visa } from '@/assets/icons/visa.svg';
import Bancontact from '@/assets/payment_methods/bancontact.png';
import OricoFinancing from '@/assets/payment_methods/orico_financing.png';
import { ReactComponent as PaybybankUS } from '@/assets/payment_methods/paybybank-us.svg';
import { ReactComponent as V12Finance } from '@/assets/payment_methods/v12.svg';
import { ReactComponent as Zip } from '@/assets/payment_methods/zip.svg';

type CardPaymentType =
  | 'visa'
  | 'mc'
  | 'amex'
  | 'cup'
  | 'discover'
  | 'diners'
  | 'jcb';

interface CardPaymentComponent {
  img: ReactNode;
  type: CardPaymentType;
}

interface PaymentMethodComponent {
  brands?: Array<CardPaymentComponent>;
  img?: ReactNode;
  type: PaymentType;
}

function getAvailableMethodByBrand(brand: string) {
  switch (brand) {
    case 'visa':
      return {
        type: brand,
        img: <Visa key={brand} data-test-id='PaymentMethodComponent-Visa' />,
      };
    case 'mc':
      return {
        type: brand,
        img: (
          <Mastercard
            key={brand}
            data-test-id='PaymentMethodComponent-Mastercard'
          />
        ),
      };
    case 'discover':
      return {
        type: brand,
        img: (
          <span key={brand} data-test-id='PaymentMethodComponent-discover'>
            <NextImage
              alt='Discover'
              height={33}
              src={DiscoverLogo}
              width={54}
            />
          </span>
        ),
      };
    case 'diners':
      return {
        type: brand,
        img: (
          <Dinersclub
            key={brand}
            data-test-id='PaymentMethodComponent-Dinersclub'
          />
        ),
      };
    case 'jcb':
      return {
        type: brand,
        img: <JCB key={brand} data-test-id='PaymentMethodComponent-JCB' />,
      };
    case 'amex':
      return {
        type: brand,
        img: (
          <AmericanExpress
            key={brand}
            data-test-id='PaymentMethodComponent-AmericanExpress'
          />
        ),
      };
    case 'cup':
      return {
        type: brand,
        img: (
          <Unionpay
            key={brand}
            data-test-id='PaymentMethodComponent-Unionpay'
          />
        ),
      };
  }
}

export const getPaymentMethodsLogos = (
  paymentMethods: PaymentMethodConfiguration[],
  isApplePayAvailable: boolean | null,
) =>
  paymentMethods.reduce<Array<PaymentMethodComponent | CardPaymentComponent>>(
    (availableMethods, paymentMethod) => {
      if (paymentMethod.type === 'scheme') {
        const brands = paymentMethod.dropInConfiguration?.brands as string[];
        brands.forEach((brand) => {
          const method = getAvailableMethodByBrand(brand);
          if (method) {
            availableMethods.push(method);
          }
        });
      }

      if (paymentMethod.type === 'applepay' && isApplePayAvailable) {
        availableMethods.push({
          type: paymentMethod.type,
          img: (
            <ApplePay
              key={paymentMethod.type}
              data-test-id='PaymentMethodComponent-ApplePay'
            />
          ),
        });
      }

      if (
        (
          [
            PAYMENT_TYPES.GOOGLE_PAY,
            PAYMENT_TYPES.PAY_WITH_GOOGLE,
          ] as PaymentType[]
        )?.includes(paymentMethod.type)
      ) {
        availableMethods.push({
          type: paymentMethod.type,
          img: (
            <GooglePay
              key={paymentMethod.type}
              data-test-id='PaymentMethodComponent-GooglePay'
            />
          ),
        });
      }

      const isOneyPayment = checkIsOneyPaymentByType(paymentMethod.type);
      if (isOneyPayment) {
        availableMethods.push({
          type: paymentMethod.type,
          img: <Oney key={paymentMethod.type} />,
        });
      }

      switch (paymentMethod.type) {
        case PAYMENT_TYPES.ALIPAY:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Alipay
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Alipay'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.AFFIRM:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Affirm
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Affirm'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.PAYPAL:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Paypal
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Paypal'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.KLARNA_PAYNOW:
        case PAYMENT_TYPES.KLARNA_ACCOUNT:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Klarna
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Klarna'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.IDEAL:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Ideal
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Ideal'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.UNION_PAY:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Unionpay
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-Unionpay'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.V12:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <V12Finance
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-v12'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.ORICO_FINANCING: // Within the ReactComponent, the gradient color cannot be correctly parsed, so use png instead.
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <NextImage
                key={paymentMethod.type}
                alt='OricoFinancing'
                data-test-id='PaymentMethodComponent-Orico'
                height='auto'
                src={OricoFinancing}
                width='54'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.TWINT:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Image
                key={paymentMethod.type}
                alt='Twint'
                data-test-id='PaymentMethodComponent-Twint'
                height={33}
                src={TwintLogo}
                width={56}
              />
            ),
          });
          break;
        case PAYMENT_TYPES.BANCONTACT: // Within the ReactComponent, the gradient color cannot be correctly parsed, so use png instead.
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <NextImage
                key={paymentMethod.type}
                alt='Bancontact'
                data-test-id='PaymentMethodComponent-bcmc'
                height='auto'
                src={Bancontact}
                width='54'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.PAYBYBANK:
        case PAYMENT_TYPES.PAYBYBANK_US:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <PaybybankUS
                key={paymentMethod.type}
                data-test-id={`PaymentMethodComponent-${paymentMethod.type}`}
              />
            ),
          });
          break;
        case PAYMENT_TYPES.CEMBRA:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Cembra
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-cembra'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.CREDIT_PLUS:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <CreditPlus
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-creditPlus'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.AGOS:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Agos
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-agos'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.ZIP:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Zip
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-zip'
              />
            ),
          });
          break;
        case PAYMENT_TYPES.SMBC:
          availableMethods.push({
            type: paymentMethod.type,
            img: (
              <Smbc
                key={paymentMethod.type}
                data-test-id='PaymentMethodComponent-smbc'
              />
            ),
          });
          break;
      }

      return availableMethods;
    },
    [],
  );

export const getPaymentMethodLogoByName = (
  paymentMethodName?: PaymentMethodName,
) => {
  switch (paymentMethodName) {
    case 'AFFIRM':
      return <Affirm data-test-id='PaymentMethodComponent-affirm' />;
    case 'AGOS':
      return <Agos data-test-id='PaymentMethodComponent-agos' />;
    case 'ALIPAY':
      return <Alipay data-test-id='PaymentMethodComponent-alipay' />;
    case 'AMEX':
      return <AmericanExpress data-test-id='PaymentMethodComponent-amex' />;
    case 'APPLEPAY':
    case 'APPLEPAY_EXPRESS':
      return <ApplePay data-test-id='PaymentMethodComponent-applepay' />;
    case 'BANCONTACT':
      return (
        <span data-test-id='PaymentMethodComponent-bancontact'>
          <NextImage
            alt='Bancontact'
            height='auto'
            src={Bancontact}
            width='54'
          />
        </span>
      );
    case 'CEMBRA_INSTALLMENT':
      return (
        <Cembra data-test-id='PaymentMethodComponent-cembra_installment' />
      );
    case 'CEMBRA_INVOICE':
      return <Cembra data-test-id='PaymentMethodComponent-cembra_invoice' />;
    case 'CREDIT_PLUS':
      return <CreditPlus data-test-id='PaymentMethodComponent-credit_plus' />;
    case 'CUP':
      return <Unionpay data-test-id='PaymentMethodComponent-cup' />;
    case 'DINERS':
      return <Dinersclub data-test-id='PaymentMethodComponent-diners' />;
    case 'DISCOVER':
      return (
        <span data-test-id='PaymentMethodComponent-discover'>
          <NextImage alt='Discover' height={33} src={DiscoverLogo} width={54} />
        </span>
      );
    case 'GOOGLEPAY':
      return <GooglePay data-test-id='PaymentMethodComponent-googlepay' />;
    case 'IDEAL':
      return <Ideal data-test-id='PaymentMethodComponent-ideal' />;
    case 'JCB':
      return <JCB data-test-id='PaymentMethodComponent-jcb' />;
    case 'KLARNA':
      return <Klarna data-test-id='PaymentMethodComponent-klarna' />;
    case 'MC':
      return <Mastercard data-test-id='PaymentMethodComponent-mc' />;
    case 'ONEY':
      return <Oney data-test-id='PaymentMethodComponent-oney' />;
    case 'ORICO_FINANCING':
      return (
        <span data-test-id='PaymentMethodComponent-orico_financing'>
          <NextImage
            alt='OricoFinancing'
            height='auto'
            src={OricoFinancing}
            width='54'
          />
        </span>
      );
    case 'PAYBYBANK':
      return <PaybybankUS data-test-id='PaymentMethodComponent-paybybank' />;
    case 'PAYPAL':
    case 'PAYPAL_EXPRESS':
      return <Paypal data-test-id='PaymentMethodComponent-paypal' />;
    case 'TWINT':
      return (
        <span data-test-id='PaymentMethodComponent-twint'>
          <NextImage alt='Twint' height={33} src={TwintLogo} width={56} />
        </span>
      );
    case 'V12':
      return <V12Finance data-test-id='PaymentMethodComponent-v12' />;
    case 'VISA':
      return <Visa data-test-id='PaymentMethodComponent-visa' />;
    case 'ZIP':
      return <Zip data-test-id='PaymentMethodComponent-zip' />;
    case 'SMBC':
      return <Smbc data-test-id='PaymentMethodComponent-smbc' />;
  }
};

export const getPaymentMethodLogoByType = (type?: PaymentType | null) => {
  switch (type) {
    case PAYMENT_TYPES.ALIPAY:
      return <Alipay data-test-id='PaymentMethodComponent-alipay' />;
    case PAYMENT_TYPES.APPLE_PAY:
      return <ApplePay data-test-id='PaymentMethodComponent-applepay' />;
    case PAYMENT_TYPES.PAY_WITH_GOOGLE:
    case PAYMENT_TYPES.GOOGLE_PAY:
      return <GooglePay data-test-id='PaymentMethodComponent-googlepay' />;
    case PAYMENT_TYPES.UNION_PAY:
      return <Unionpay data-test-id='PaymentMethodComponent-unionpay' />;
    case PAYMENT_TYPES.PAYPAL_EXPRESS:
    case PAYMENT_TYPES.PAYPAL:
      return <Paypal data-test-id='PaymentMethodComponent-paypal' />;
    case PAYMENT_TYPES.KLARNA_PAYNOW:
      return <Klarna data-test-id='PaymentMethodComponent-klarna_paynow' />;
    case PAYMENT_TYPES.KLARNA_ACCOUNT:
      return <Klarna data-test-id='PaymentMethodComponent-klarna_account' />;
    case PAYMENT_TYPES.AFFIRM:
      return <Affirm data-test-id='PaymentMethodComponent-affirm' />;
    case PAYMENT_TYPES.IDEAL:
      return <Ideal data-test-id='PaymentMethodComponent-ideal' />;
    case PAYMENT_TYPES.TWINT:
      return <NextImage alt='Twint' height={33} src={TwintLogo} width={56} />;
    case PAYMENT_TYPES.BANCONTACT:
      return (
        <NextImage alt='Bancontact' height='auto' src={Bancontact} width='54' />
      );
    case PAYMENT_TYPES.FACILYPAY_3X:
    case PAYMENT_TYPES.FACILYPAY_4X:
      return <Oney data-test-id='PaymentMethodComponent-facilypay_3x' />;
    case PAYMENT_TYPES.PAYBYBANK:
    case PAYMENT_TYPES.PAYBYBANK_US:
      return <PaybybankUS data-test-id='PaymentMethodComponent-paybybank' />;
    case PAYMENT_TYPES.V12:
      return <V12Finance data-test-id='PaymentMethodComponent-v12' />;
    case PAYMENT_TYPES.CEMBRA:
    case PAYMENT_TYPES.CEMBRA_INVOICE:
      return (
        <Cembra data-test-id='PaymentMethodComponent-cembra_installment' />
      );
    case PAYMENT_TYPES.CREDIT_PLUS:
      return <CreditPlus data-test-id='PaymentMethodComponent-creditplus' />;
    case PAYMENT_TYPES.AGOS:
      return <Agos data-test-id='PaymentMethodComponent-agos' />;
    case PAYMENT_TYPES.ZIP:
      return <Zip data-test-id='PaymentMethodComponent-zip' />;
    case PAYMENT_TYPES.VISA:
      return <Visa data-test-id='PaymentMethodComponent-visa' />;
    case PAYMENT_TYPES.ORICO_FINANCING:
      return (
        <NextImage
          alt='OricoFinancing'
          height='auto'
          src={OricoFinancing}
          width='54'
        />
      );
    case PAYMENT_TYPES.SMBC:
      return <Smbc data-test-id='PaymentMethodComponent-smbc' />;
  }
};

export const getPaymentMethodLogo = (
  paymentMethodName?: PaymentMethodName,
  type?: PaymentType | null,
) => {
  return (
    getPaymentMethodLogoByName(paymentMethodName) ??
    getPaymentMethodLogoByType(type) ?? <CreditCard />
  );
};
